import {
  FileMetadata,
  FileUploadParams,
  FileUpdateParams,
  ListFilesParams,
  PaginatedResponse,
  Folder,
  FolderTreeNode,
  FolderContentsResponse,
  CreateFolderParams,
  UpdateFolderParams,
  MoveFolderParams,
  MoveFileParams,
  LinkExternalFileParams,
  LinkExternalFolderParams,
  LinkMultipleParams,
  LinkMultipleResponse,
  ExternalStorageListResponse,
  BrowseExternalStorageParams,
  SearchExternalStorageParams,
  CreateTextFileParams,
  FileAccessMode,
  MalwareScanStatus } from '../types';
import { apiGet, apiPost, apiPut, apiDelete, getValidAccessToken } from '../../../shared/api/axiosInstance';
import { API_BASE_URL } from '../../../shared/api/config';

const FILES_ENDPOINT = '/api/v1/files';
const FOLDERS_ENDPOINT = '/api/v1/folders';
const EXTERNAL_STORAGE_ENDPOINT = '/api/v1/external-storage';

/**
 * Resolve the download endpoint + whether it needs an auth token, by access mode.
 *
 * Security F5: the `/link` route is no longer "anyone with the link" — it now
 * requires authentication and gates entity attachments on the caller's relation
 * to the parent. So only genuinely public assets (`access_mode='public'`) stay on
 * the unauthenticated `/public` route; link attachments and private files must be
 * fetched with a bearer token.
 */
const resolveDownloadEndpoint = (
  id: string,
  accessMode?: FileAccessMode
): { endpoint: string; requiresAuth: boolean } => {
  if (accessMode === 'public') {
    return { endpoint: `${API_BASE_URL}${FILES_ENDPOINT}/${id}/public`, requiresAuth: false };
  }
  if (accessMode === 'link') {
    return { endpoint: `${API_BASE_URL}${FILES_ENDPOINT}/${id}/link`, requiresAuth: true };
  }
  return { endpoint: `${API_BASE_URL}${FILES_ENDPOINT}/${id}/download`, requiresAuth: true };
};

// ============== File API ==============

export const fileApi = {
  upload: async (params: FileUploadParams): Promise<{ data: FileMetadata }> => {
    const formData = new FormData();
    formData.append('file', params.file);
    if (params.description) formData.append('description', params.description);
    if (params.folder_id) formData.append('folder_id', params.folder_id);
    if (params.is_public) formData.append('is_public', 'true');
    if (params.is_user_file === false) formData.append('is_user_file', 'false');
    if (params.attached_to) formData.append('attached_to', params.attached_to);
    if (params.company_id) formData.append('company_id', params.company_id);

    const token = await getValidAccessToken();

    const response = await fetch(`${API_BASE_URL}${FILES_ENDPOINT}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw error;
    }
    return response.json();
  },

  list: async (params?: ListFilesParams): Promise<PaginatedResponse<FileMetadata>> => {
    const searchParams = new URLSearchParams();
    if (params?.mime_type) searchParams.set('mime_type', params.mime_type);
    if (params?.folder_id) searchParams.set('folder_id', params.folder_id);
    if (params?.is_user_file !== undefined) searchParams.set('is_user_file', String(params.is_user_file));
    if (params?.attached_to) searchParams.set('attached_to', params.attached_to);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const url = `${FILES_ENDPOINT}${searchParams.toString() ? `?${searchParams}` : ''}`;
    return apiGet(url);
  },

  /**
   * Batch attachment-preview fetch: files grouped by `attached_to` ref, in ONE
   * request. Collapses the per-row `list({ attached_to })` N+1 that list tables
   * (e.g. products) used to fire — that per-row storm blew past the file-listing
   * endpoint's per-tenant in-flight cap and surfaced as a "Rate limit exceeded"
   * toast. Refs the caller can't access (parent ReBAC) are simply absent from the
   * returned map; callers treat an absent ref as "no attachments".
   */
  listAttachmentsBatch: async (
    refs: string[]
  ): Promise<{ data: Record<string, FileMetadata[]> }> => {
    return apiPost(`${FILES_ENDPOINT}/attachments/batch`, { refs });
  },

  get: async (id: string): Promise<{ data: FileMetadata }> => {
    return apiGet(`${FILES_ENDPOINT}/${id}`);
  },

  getScanStatus: async (id: string): Promise<{ data: { malware_scan_status: MalwareScanStatus } }> => {
    return apiGet(`${FILES_ENDPOINT}/${id}/scan-status`);
  },

  update: async (id: string, data: FileUpdateParams): Promise<{ data: FileMetadata }> => {
    return apiPut(`${FILES_ENDPOINT}/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    return apiDelete(`${FILES_ENDPOINT}/${id}`);
  },

  createTextFile: async (params: CreateTextFileParams): Promise<{ data: FileMetadata }> => {
    return apiPost(`${FILES_ENDPOINT}/create`, params);
  },

  move: async (id: string, data: MoveFileParams): Promise<{ data: FileMetadata }> => {
    return apiPost(`${FILES_ENDPOINT}/${id}/move`, data);
  },

  linkExternal: async (params: LinkExternalFileParams): Promise<{ data: FileMetadata }> => {
    return apiPost(`${FILES_ENDPOINT}/link-external`, params);
  },

  linkMultiple: async (params: LinkMultipleParams): Promise<{ data: LinkMultipleResponse }> => {
    return apiPost(`${FILES_ENDPOINT}/link-multiple`, params);
  },

  syncExternal: async (id: string): Promise<{ data: FileMetadata }> => {
    return apiPost(`${FILES_ENDPOINT}/${id}/sync`, {});
  },

  unlinkExternal: async (id: string): Promise<void> => {
    return apiPost(`${FILES_ENDPOINT}/${id}/unlink`, {});
  },

  /**
   * Resolve a directly-loadable URL for an authorized file — the robust path for
   * the two things the browser can't do via a cross-origin `fetch`: display an
   * `<img src>` and navigate a download.
   *
   * On S3 the backend returns a short-lived presigned URL: the browser loads it
   * directly (governed by CSP `img-src https:` / plain navigation), so the bytes
   * still flow S3→client but **no S3 CORS and no CSP `connect-src` bucket entry**
   * are involved — it works on every hostname/CloudFront distribution regardless
   * of bucket-CORS drift (the failure that broke attachment thumbnails in prod).
   * On the local backend, or for external files, the URL is `null`; the caller
   * then falls back to a same-origin blob fetch (`fetchAsBlob`), which streams and
   * is likewise CORS-free.
   *
   * `disposition`: `inline` for display, `attachment` for download.
   */
  fetchDownloadUrl: async (
    id: string,
    disposition: 'inline' | 'attachment'
  ): Promise<string | null> => {
    const res = await apiGet<{ data: { url: string | null } }>(
      `${FILES_ENDPOINT}/${id}/download-url?disposition=${disposition}`
    );
    return res.data.url;
  },

  getPublicUrl: (id: string): string => {
    // Use relative URL so browsers load it same-origin (via Vite proxy in dev,
    // same host in prod), avoiding Cross-Origin-Resource-Policy blocking on <img> tags.
    return `${FILES_ENDPOINT}/${id}/public`;
  },

  /** Fetch file as blob. Genuinely public files use the unauthenticated /public
   *  route; link attachments and private files require an auth token (the /link
   *  route is now parent-ReBAC gated — Security F5). */
  fetchAsBlob: async (id: string, accessMode?: FileAccessMode): Promise<Blob> => {
    const { endpoint, requiresAuth } = resolveDownloadEndpoint(id, accessMode);

    const headers: Record<string, string> = {};
    if (requiresAuth) {
      const token = await getValidAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(endpoint, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch file');
    }

    return response.blob();
  },

  download: async (id: string, filename: string, accessMode?: FileAccessMode): Promise<void> => {
    // Preferred path (S3): navigate an anchor straight to the presigned URL. The
    // signed Content-Disposition=attachment (filename included) drives the
    // download, and a cross-origin navigation needs no CORS — so this is robust on
    // every hostname/distribution. Public files are same-origin (/public) and
    // never need this; local/external return null → same-origin blob fallback below.
    if (accessMode !== 'public') {
      const directUrl = await fileApi.fetchDownloadUrl(id, 'attachment');
      if (directUrl) {
        const link = document.createElement('a');
        link.href = directUrl;
        // `download` is honored same-origin; cross-origin it is ignored and the
        // signed Content-Disposition supplies the filename — either way correct.
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
    }

    // Fallback (local backend / public / external): fetch the bytes same-origin
    // (these routes stream, no redirect) and save via an object URL.
    const blob = await fileApi.fetchAsBlob(id, accessMode);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /** Upload a file from a URL - useful for downloading external logos/images */
  uploadFromUrl: async (params: {
    url: string;
    filename?: string;
    description?: string;
    is_public?: boolean;
    is_user_file?: boolean;
    attached_to?: string;
  }): Promise<{ data: FileMetadata }> => {
    return apiPost(`${FILES_ENDPOINT}/from-url`, params);
  },
};

// ============== Folder API ==============

export const folderApi = {
  list: async (): Promise<{ data: Folder[] }> => {
    return apiGet(FOLDERS_ENDPOINT);
  },

  get: async (id: string): Promise<{ data: Folder }> => {
    return apiGet(`${FOLDERS_ENDPOINT}/${id}`);
  },

  getContents: async (id: string, limit?: number, offset?: number): Promise<FolderContentsResponse> => {
    const searchParams = new URLSearchParams();
    if (limit) searchParams.set('limit', String(limit));
    if (offset) searchParams.set('offset', String(offset));
    const query = searchParams.toString() ? `?${searchParams}` : '';
    return apiGet(`${FOLDERS_ENDPOINT}/${id}/contents${query}`);
  },

  getRootContents: async (limit?: number, offset?: number, sharedWithMe?: boolean): Promise<FolderContentsResponse> => {
    const searchParams = new URLSearchParams();
    if (limit) searchParams.set('limit', String(limit));
    if (offset) searchParams.set('offset', String(offset));
    if (sharedWithMe) searchParams.set('shared_with_me', 'true');
    const query = searchParams.toString() ? `?${searchParams}` : '';
    return apiGet(`${FOLDERS_ENDPOINT}/root/contents${query}`);
  },

  getBreadcrumbs: async (id: string): Promise<{ data: Folder[] }> => {
    return apiGet(`${FOLDERS_ENDPOINT}/${id}/breadcrumbs`);
  },

  getTree: async (maxDepth?: number, sharedWithMe?: boolean): Promise<{ data: FolderTreeNode[] }> => {
    const searchParams = new URLSearchParams();
    if (maxDepth) searchParams.set('max_depth', String(maxDepth));
    if (sharedWithMe) searchParams.set('shared_with_me', 'true');
    const query = searchParams.toString() ? `?${searchParams}` : '';
    return apiGet(`${FOLDERS_ENDPOINT}/tree${query}`);
  },

  create: async (params: CreateFolderParams): Promise<{ data: Folder }> => {
    return apiPost(FOLDERS_ENDPOINT, params);
  },

  update: async (id: string, data: UpdateFolderParams): Promise<{ data: Folder }> => {
    return apiPut(`${FOLDERS_ENDPOINT}/${id}`, data);
  },

  delete: async (id: string, recursive?: boolean): Promise<void> => {
    const query = recursive ? '?recursive=true' : '';
    return apiDelete(`${FOLDERS_ENDPOINT}/${id}${query}`);
  },

  move: async (id: string, data: MoveFolderParams): Promise<{ data: Folder }> => {
    return apiPost(`${FOLDERS_ENDPOINT}/${id}/move`, data);
  },

  linkExternal: async (params: LinkExternalFolderParams): Promise<{ data: Folder }> => {
    return apiPost(`${FOLDERS_ENDPOINT}/link-external`, params);
  },

  syncExternal: async (id: string): Promise<{ data: Folder }> => {
    return apiPost(`${FOLDERS_ENDPOINT}/${id}/sync`, {});
  },

  unlinkExternal: async (id: string): Promise<void> => {
    return apiPost(`${FOLDERS_ENDPOINT}/${id}/unlink`, {});
  } };

// ============== External Storage API ==============

export const externalStorageApi = {
  browse: async (params: BrowseExternalStorageParams): Promise<{ data: ExternalStorageListResponse }> => {
    const searchParams = new URLSearchParams();
    if (params.folder_id) searchParams.set('folder_id', params.folder_id);
    if (params.query) searchParams.set('query', params.query);
    if (params.max_results) searchParams.set('max_results', String(params.max_results));
    if (params.page_token) searchParams.set('page_token', params.page_token);
    if (params.oauth_connection_id) searchParams.set('oauth_connection_id', params.oauth_connection_id);

    const query = searchParams.toString() ? `?${searchParams}` : '';
    return apiGet(`${EXTERNAL_STORAGE_ENDPOINT}/${params.provider}/browse${query}`);
  },

  search: async (params: SearchExternalStorageParams): Promise<{ data: ExternalStorageListResponse }> => {
    const searchParams = new URLSearchParams();
    searchParams.set('query', params.query);
    if (params.max_results) searchParams.set('max_results', String(params.max_results));
    if (params.page_token) searchParams.set('page_token', params.page_token);

    return apiGet(`${EXTERNAL_STORAGE_ENDPOINT}/${params.provider}/search?${searchParams}`);
  } };
